/**
 * Session Debug Panel Component
 *
 * A floating debug panel that can be toggled to show session loading debug information.
 * This is useful for developers and support team to diagnose session loading issues.
 */

import React, { useState, useEffect } from 'react';
import { Card, Badge, Button, Collapse, Alert } from 'react-bootstrap';
import { sessionDebugger } from '../../utilities/session-debug';

function SessionDebugPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const [debugReport, setDebugReport] = useState(null);
  const [autoUpdate, setAutoUpdate] = useState(true);

  // Auto-update the debug report every 2 seconds when panel is open
  useEffect(() => {
    if (!isOpen || !autoUpdate) return;

    const interval = setInterval(() => {
      if (sessionDebugger.isEnabled) {
        setDebugReport(sessionDebugger.getDebugReport());
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [isOpen, autoUpdate]);

  // Initial load when panel opens
  useEffect(() => {
    if (isOpen && sessionDebugger.isEnabled) {
      setDebugReport(sessionDebugger.getDebugReport());
    }
  }, [isOpen]);

  if (!sessionDebugger.isEnabled) {
    return null;
  }

  const handleRefresh = () => {
    setDebugReport(sessionDebugger.getDebugReport());
  };

  const handleDownload = (format) => {
    sessionDebugger.downloadReport(null, format);
  };

  const handleClear = () => {
    sessionDebugger.clear();
    setDebugReport(null);
  };

  const formatElapsed = (ms) => {
    if (ms > 1000) return `${(ms / 1000).toFixed(1)}s`;
    return `${ms}ms`;
  };

  const getStepBadgeVariant = (step) => {
    if (step.includes('ERROR') || step.includes('FAILURE')) return 'danger';
    if (step.includes('SUCCESS') || step === 'READY_TO_RENDER')
      return 'success';
    if (step.includes('START') || step === 'INIT') return 'primary';
    return 'secondary';
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 9999,
        minWidth: '300px',
        maxWidth: '500px',
      }}
    >
      <Card bg="dark" text="white" style={{ fontSize: '12px' }}>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span>
            <strong>Session Debug</strong>
            {debugReport && (
              <Badge variant="info" className="ml-2">
                {debugReport.debugLevelName}
              </Badge>
            )}
          </span>
          <div>
            <Button
              variant="outline-light"
              size="sm"
              onClick={() => setIsOpen(!isOpen)}
              className="mr-1"
            >
              {isOpen ? '−' : '+'}
            </Button>
          </div>
        </Card.Header>

        <Collapse in={isOpen}>
          <Card.Body>
            {debugReport ? (
              <>
                {debugReport.summary.issues.length > 0 && (
                  <Alert variant="warning" className="mb-2 p-2">
                    <strong>Issues Detected:</strong>
                    <ul className="mb-0 mt-1">
                      {debugReport.summary.issues.map((issue, i) => (
                        <li key={i}>{issue}</li>
                      ))}
                    </ul>
                  </Alert>
                )}

                <div className="mb-2">
                  <strong>Session:</strong> {debugReport.sessionKey || 'None'}
                  <br />
                  <strong>Total Time:</strong>{' '}
                  {formatElapsed(debugReport.totalElapsed)}
                  <br />
                  <strong>Steps:</strong> {debugReport.summary.stepsCompleted}
                  <br />
                  <strong>Errors:</strong> {debugReport.summary.errorCount}
                </div>

                {Object.keys(debugReport.steps).length > 0 && (
                  <div className="mb-2">
                    <strong>Loading Steps:</strong>
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {Object.entries(debugReport.steps).map(([step, info]) => (
                        <div
                          key={step}
                          className="d-flex justify-content-between align-items-center mb-1"
                        >
                          <Badge
                            variant={getStepBadgeVariant(step)}
                            className="mr-2"
                          >
                            {step.replace('SESSION_LOAD_STEPS.', '')}
                          </Badge>
                          <small>{formatElapsed(info.elapsed)}</small>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {debugReport.errors.length > 0 && (
                  <div className="mb-2">
                    <strong>Recent Errors:</strong>
                    <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                      {debugReport.errors.slice(-3).map((error, i) => (
                        <Alert key={i} variant="danger" className="p-2 mb-1">
                          <strong>{error.context}:</strong> {error.message}
                        </Alert>
                      ))}
                    </div>
                  </div>
                )}

                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={handleRefresh}
                      className="mr-1"
                    >
                      Refresh
                    </Button>
                    <Button
                      variant="outline-warning"
                      size="sm"
                      onClick={() => setAutoUpdate(!autoUpdate)}
                      className="mr-1"
                    >
                      {autoUpdate ? 'Pause' : 'Resume'}
                    </Button>
                  </div>
                  <div>
                    <Button
                      variant="outline-success"
                      size="sm"
                      onClick={() => handleDownload('json')}
                      className="mr-1"
                    >
                      JSON
                    </Button>
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={() => handleDownload('text')}
                      className="mr-1"
                    >
                      TXT
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={handleClear}
                    >
                      Clear
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center">
                <p>No debug data available</p>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleRefresh}
                >
                  Load Debug Data
                </Button>
              </div>
            )}
          </Card.Body>
        </Collapse>
      </Card>
    </div>
  );
}

export default SessionDebugPanel;
